import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedStock } from '../../contexts/SelectedStockContext';
import { useStrategy } from '../../contexts/StrategyContext';
import { StockScreenerData } from './columnDefinitions';
import FullscreenChartModal from './FullscreenChartModal';
import StockAnalysisModal from './StockAnalysisModal';
import MiniStockChart from './MiniStockChart';
import ScoreBreakdown from './ScoreBreakdown';
import SetupMatrixPopover from './SetupMatrixPopover';
import { displayCell } from '../common/SetupIcon';
import DataFreshnessIndicator from '../portfolio/DataFreshnessIndicator';
import LatestPriceChip from '../common/LatestPriceChip';
import { LatestStockQuote } from '../../types/latestPrice';
import { CapEntry } from './columnDefinitions';
import {
  cardFlags,
  describeSetupCell,
  fmtSignedPct,
  fmtMarketCap,
  ivRankLabel,
  FREQUENCY_LABEL,
} from '../../utils/screenerCard';
import { fmtPlainDate } from '../../utils/plainDate';
import { NORMAL_CC_WINDOW_DAYS } from '../../utils/earnings';

interface WizardFilters {
  minDays: number;
  maxDays: number;
  minDelta: number;
  maxDelta: number;
  ownedStocks?: string[];
  min_net_margin?: number;
  max_debt_equity?: number;
  min_iv_rank?: number;
  min_rsi?: number;
  max_rsi?: number;
  trend_filter?: string;
}

interface StockCardProps {
  stock: StockScreenerData;
  chartData?: { results: any[] }; // Optional pre-loaded chart data for performance
  wizardFilters?: WizardFilters; // Optional wizard filters to pass to analysis page
  strategyType?: string; // e.g., "Keep Shares" or "Balanced Approach" for display
  lastUpdated?: string | null; // Timestamp of last data refresh for freshness indicator
  /** Position in the current sort order (1-based); shown as "#n". */
  rank?: number;
  /** Latest price (delayed) chip; the Price above stays the 15-min mark. */
  latest?: LatestStockQuote | null;
}

const pnlClass = (v: number) => (v >= 0 ? 'sc2-pos' : 'sc2-neg');


const fmtPct = (v: number | null | undefined, digits = 1) => (v != null && !isNaN(v) ? `${v.toFixed(digits)}%` : '—');

const fmtScore = (v: number | null | undefined) => (v != null && !isNaN(v) ? Math.round(v) : '—');

const AlertIcon: React.FC = () => (
  <svg className="sc2-ic" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M8 1.8 15 14H1L8 1.8Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 6.2v3.6M8 11.6v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CHEVRON_TURN = { down: undefined, up: 'rotate(180deg)', right: 'rotate(-90deg)' } as const;
const ChevronIcon: React.FC<{ dir?: keyof typeof CHEVRON_TURN }> = ({ dir = 'down' }) => (
  <svg className="sc2-ic" viewBox="0 0 16 16" aria-hidden="true" style={CHEVRON_TURN[dir] ? { transform: CHEVRON_TURN[dir] } : undefined}>
    <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Screener stock card (v2, Oct 2026 UX review).
 *
 * Layout "B+": two blocks, one per score dimension, on a shared 3-column grid.
 * Income (Income fit): what it pays and how calls (puts) on this stock have done.
 * Setup (Timing): today's setup, how it compares with the stock's usual result, and
 * how often it occurs; the matrix is the evidence, one tap away. The CC Score sits
 * by the ticker. Flags only when they apply. No verdict words (no-advice rule).
 */
const StockCard: React.FC<StockCardProps> = ({ stock, chartData, wizardFilters, strategyType, lastUpdated, rank, latest }) => {
  const navigate = useNavigate();
  const { selectedStock, setSelectedStock } = useSelectedStock();
  const { strategy } = useStrategy();
  const isCsp = strategy === 'csp';
  const contract = isCsp ? 'put' : 'call';

  const [showChartModal, setShowChartModal] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showDetails) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    detailsRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
  }, [showDetails]);
  const [showMatrix, setShowMatrix] = useState(false);
  const [openFlag, setOpenFlag] = useState<string | null>(null);
  const matrixAnchorRef = useRef<HTMLButtonElement>(null);
  const closeMatrix = useCallback(() => setShowMatrix(false), []);

  // Price change flash: tint the background only. The price text stays neutral so a
  // 1¢ uptick on a down day never shows a green price next to a red change.
  const prevPriceRef = useRef<number | null>(stock.current_price);
  const [priceFlash, setPriceFlash] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    const prev = prevPriceRef.current;
    prevPriceRef.current = stock.current_price;
    if (prev != null && stock.current_price != null && prev !== stock.current_price) {
      setPriceFlash(stock.current_price > prev ? 'up' : 'down');
      const timer = setTimeout(() => setPriceFlash(null), 600);
      return () => clearTimeout(timer);
    }
  }, [stock.current_price]);

  const isSelected = selectedStock?.symbol === stock.symbol;

  const goToContracts = () => {
    navigate(`/analysis?symbol=${stock.symbol}`, {
      state: { wizardFilters, strategyType },
    });
  };

  const handleCardClick = () => {
    // Desktop: select the card. Phones: no hidden navigation; "Pick a contract" says where it goes.
    if (window.innerWidth < 1024) return;
    setSelectedStock(isSelected ? null : stock);
  };

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // ---- Data for the three answers (all computed by the backend, gold phase 2b) --------
  const flags = cardFlags(stock, isCsp);
  const setupName = stock.setup_cell ? displayCell(stock.setup_cell) : null;
  const setupDesc = describeSetupCell(stock.setup_cell);

  const change = stock.change_percent;
  const sector = stock.sector;
  const marketCap = fmtMarketCap(stock.market_cap);
  const reasons = new Set(stock.liquidity?.reasons ?? []);
  const rule = stock.liquidity?.rule;
  const earnDate = fmtPlainDate(stock.next_earnings_date);
  const earnDays = stock.days_to_earnings;
  // Caps of the default score, already split by dimension and named after their rule.
  // A custom framework has no caps (the backend sends none next to the user's score).
  const incomeCaps = stock.income_fit_caps ?? [];
  const timingCaps = stock.timing_caps ?? [];
  const notScored = !!stock.not_scored;
  // Liquidity rule v2, CSP lens: no standard put -> the CSP score stays blank (backend rule no_standard_put).
  const noStandardPut = !!stock.no_standard_put;
  const capText = (cs: CapEntry[]) => cs.map((c) => (c.detail ? `${c.label} (${c.detail})` : c.label)).join(', ');

  // The contract behind the premium figure: one delta, one duration.
  const callUsed = stock.best_dte != null
    ? `${stock.best_delta != null ? `${stock.best_delta.toFixed(2)}Δ · ` : ''}${stock.best_dte}d`
    : null;

  return (
    <div
      className={`stock-card sc2 ${isSelected ? 'stock-card-selected' : ''}`}
      onClick={handleCardClick}
    >
      {/* Identity: ticker + overall score | price; company | today's change */}
      <div className="sc2-id">
        <h3 className="sc2-ticker">
          {rank != null && <span className="sc2-rank" title="Position in the current sort order">#{rank}</span>}
          {stock.symbol}
          {stock.score != null && (
            <span
              className="sc2-score"
              title={`${isCsp ? 'CSP' : 'CC'} score ${Math.round(stock.score)} / 100: how good this stock is to sell ${contract}s on right now. Income fit (${fmtScore(stock.income_fit)}/50) + Timing (${fmtScore(stock.timing)}/50), shown below.`}
            >
              <b>{Math.round(stock.score)}</b><span>/100</span>
            </span>
          )}
          {notScored && (
            <em
              className="sc2-capped"
              title="Not scored: a leveraged or inverse fund. It resets daily and loses value over a 35-day cycle, so the premium does not survive the hold."
            >not scored</em>
          )}
          {noStandardPut && (
            <em
              className="sc2-capped"
              title="Not scored for puts: no put at delta 0.15–0.30, 30–60 days out, with open interest of at least 100 and a traded price, so there is no standard put to sell."
            >no standard put</em>
          )}
        </h3>
        <div
          className="sc2-price-group"
          style={{
            backgroundColor:
              priceFlash === 'up' ? 'rgba(16, 185, 129, 0.12)' : priceFlash === 'down' ? 'rgba(239, 68, 68, 0.12)' : 'transparent',
            transition: priceFlash ? 'none' : 'background-color 700ms ease-out',
          }}
        >
          <DataFreshnessIndicator lastUpdated={lastUpdated} type="stock" />
          <span className="sc2-price">
            {stock.current_price != null ? `$${stock.current_price.toFixed(2)}` : '—'}
          </span>
        </div>
        <span className="sc2-sub" title={stock.company_name ?? undefined}>
          {stock.company_name}
          {sector && <> · {sector}</>}
          {marketCap && <> · <span className="sc2-cap">{marketCap}</span></>}
        </span>
        <span className="sc2-change-line">
          {change != null && !isNaN(change) && (
            <span className={pnlClass(change)}>{fmtSignedPct(change)} today</span>
          )}
        </span>
        {latest?.latest_price != null && (
          <LatestPriceChip
            className="sc2-latest"
            session={latest.bar_session}
            barTime={latest.bar_time}
            delayedMinutes={latest.delayed_minutes}
            price={latest.latest_price}
            changePct={latest.change_pct}
          />
        )}
      </div>

      {/* Risk flags: only when they apply; tap a chip for the sentence */}
      {/* Always one row (empty when nothing applies) so cards line up; overflow scrolls sideways */}
      {(
        <div className={`sc2-flags ${flags.length === 0 ? 'is-empty' : ''}`} onClick={stop}>
          <ul className="sc2-chips" aria-label="Risk flags">
            {flags.map((f) => (
              <li key={f.kind}>
                <button
                  type="button"
                  className={`sc2-chip sc2-chip-${f.level} ${openFlag === f.kind ? 'is-open' : ''}`}
                  aria-expanded={openFlag === f.kind}
                  title={f.detail}
                  onClick={() => setOpenFlag((k) => (k === f.kind ? null : f.kind))}
                >
                  <AlertIcon /> {f.chip}
                </button>
              </li>
            ))}
          </ul>
          {openFlag && (
            <p className="sc2-flag-detail" role="status">
              {flags.find((f) => f.kind === openFlag)?.detail}
            </p>
          )}
        </div>
      )}

      {/* Price chart */}
      <div className="sc2-chart" onClick={stop}>
        <MiniStockChart
          symbol={stock.symbol}
          height={80}
          variant="plain"
          chartData={chartData}
          realtimePrice={stock.current_price ?? undefined}
          onOpenFullscreen={chartData?.results?.length ? () => setShowChartModal(true) : undefined}
        />
      </div>

      {/* Two columns, one KPI per row, under the score each one explains.
          Totals only: per-row points are not shown. "—" when the API has no value. */}
      <div className="sc2-two">
        <section className="sc2-col" aria-label="Income fit">
          <div className="sc2-col-h" title="Income fit: is the income worth it, and can you trade it?">
            <span>Income fit</span>
            <b>
              {incomeCaps.length > 0 && (
                <em
                  className="sc2-capped"
                  title={`Capped by: ${capText(incomeCaps)}. Options outside the liquidity rule${rule ? ` (spread ≤ ${rule.max_spread_pct}%, open interest ≥ ${rule.min_open_interest})` : ''} cap Income fit at 20; a company losing money caps it at 25.`}
                >capped</em>
              )}
              {fmtScore(stock.income_fit)}<span className="sc2-of">/50</span>
            </b>
          </div>
          <div className="sc2-row" title={`Annualized premium of the best ${contract} at delta 0.15–0.30, 30–45 days out, with open interest of at least 100${isCsp ? ', on the cash set aside' : ', on the share price'}. You pick the actual contract in step 2.`}>
            <span>Premium / yr (best)</span><b>{stock.best_ann_yield != null ? `${stock.best_ann_yield.toFixed(0)}%` : '—'}</b>
          </div>
          <div className="sc2-row" title={`The ${contract} behind the premium figure.`}>
            <span>{isCsp ? 'Put used' : 'Call used'}</span>
            <b>{callUsed ?? '—'}</b>
          </div>
          <div className="sc2-row" title={isCsp ? 'Dividend yield of the stock. With a put you hold cash, so you collect it only once assigned.' : 'Dividend yield you collect while holding the shares.'}>
            <span>Dividend / yr</span><b>{fmtPct(stock.dividend_yield)}</b>
          </div>
          <div className="sc2-row" title={`Median bid-ask spread of the standard ${contract}s (delta 0.15–0.30, 30–45 days out, open interest ≥ 100).${rule ? ` The liquidity rule allows up to ${rule.max_spread_pct}%.` : ''}`}>
            <span>Spread</span><b className={reasons.has('spread_above_max') ? 'is-flag' : ''}>{stock.spread_pct != null ? `${stock.spread_pct.toFixed(1)}%` : '—'}</b>
          </div>
          <div className="sc2-row" title={`Highest open interest among the standard ${contract}s.${rule ? ` The liquidity rule needs at least ${rule.min_open_interest}.` : ''}`}>
            <span>Max open interest</span><b className={reasons.has('oi_below_min') ? 'is-flag' : ''}>{stock.max_oi != null ? stock.max_oi.toLocaleString('en-US') : '—'}</b>
          </div>
          <div className="sc2-row" title={stock.is_fund ? 'Not shown for funds.' : 'Net profit as a share of revenue. Below zero = losing money.'}>
            <span>Net margin</span><b className={stock.net_margin != null && stock.net_margin < 0 ? 'is-neg' : ''}>{fmtPct(stock.net_margin)}</b>
          </div>
          <div className="sc2-row" title={stock.is_fund ? 'Not shown for funds.' : "Debt divided by shareholders' equity."}>
            <span>Debt / equity</span><b>{stock.debt_to_equity != null ? stock.debt_to_equity.toFixed(2) : '—'}</b>
          </div>
        </section>

        <section className="sc2-col" aria-label="Timing">
          <div className="sc2-col-h" title="Timing: is now the moment?">
            <span>Timing</span>
            <b>
              {timingCaps.length > 0 && (
                <em
                  className="sc2-capped"
                  title={`Capped by: ${capText(timingCaps)}. The score's earnings rule (earnings within 30 days, or within the scored option's life) caps Timing at 15; a losing setup caps it at 10.`}
                >capped</em>
              )}
              {fmtScore(stock.timing)}<span className="sc2-of">/50</span>
            </b>
          </div>
          <div className="sc2-row" title={stock.iv_rank_interim ? 'IV rank, 0–100, over the history available so far (under a year): where the stock\'s 30-day implied volatility sits between its lowest and highest level since then. Not a percentage.' : 'IV rank, 0–100: where the stock\'s 30-day implied volatility sits in its 1-year range (0 = lowest, 100 = highest). Not a percentage.'}>
            <span>{ivRankLabel(stock)}</span><b>{stock.iv_rank != null ? Math.round(stock.iv_rank) : '—'}</b>
          </div>
          <div className="sc2-row" title="The stock's 30-day implied volatility (IV30) vs its 20-day realized volatility. IV above HV: options price in bigger moves than the stock has made.">
            <span>IV / HV</span><b>{stock.iv != null && stock.hv != null ? `${stock.iv.toFixed(0)} / ${stock.hv.toFixed(0)}` : '—'}</b>
          </div>
          <div className="sc2-row" title={setupDesc ?? undefined}>
            {stock.setup_cell ? (
              <button
                ref={matrixAnchorRef}
                type="button"
                className="sc2-row-link"
                aria-expanded={showMatrix}
                title={`Explore ${stock.symbol}'s setups`}
                onClick={(e) => { stop(e); setShowMatrix((v) => !v); }}
              >
                Setup <ChevronIcon dir="right" />
              </button>
            ) : <span>Setup</span>}
            <b className="is-word">{setupName ?? '—'}</b>
          </div>
          <div
            className="sc2-row"
            title={
              stock.setup_basis === 'cluster'
                ? `Not enough ${stock.symbol} history in this setup; this is the average for similar stocks.`
                : `Average net P&L of a 35-day call on ${stock.symbol} in past periods when it was in this setup: premium plus the stock's move, per trade.`
            }
          >
            <span>{stock.setup_basis === 'cluster' ? "Peers' P&L in setup" : 'Net P&L in setup'}</span>
            <b className={stock.setup_pnl != null ? pnlClass(stock.setup_pnl) : ''}>{stock.setup_pnl != null ? fmtSignedPct(stock.setup_pnl) : '—'}</b>
          </div>
          <div className="sc2-row" title={`Difference with the average 35-day call on ${stock.symbol} across all setups (its usual result).`}>
            <span>vs usual</span><b>{stock.setup_vs_usual != null ? `${stock.setup_vs_usual >= 0 ? '+' : ''}${stock.setup_vs_usual.toFixed(1)} pts` : '—'}</b>
          </div>
          <div
            className="sc2-row"
            title={stock.setup_share != null
              ? `How much history backs this setup for ${stock.symbol}: it was in this setup on about ${stock.setup_share.toFixed(0)}% of its measured days${stock.setup_n != null ? ` (${stock.setup_n} days)` : ''}. Frequent ≥ 10%, occasional ≥ 5%, rare below.`
              : 'Not enough history in this setup to judge.'}
          >
            <span>How often</span><b className="is-word">{stock.setup_frequency ? FREQUENCY_LABEL[stock.setup_frequency] : '—'}</b>
          </div>
          <div className="sc2-row" title="Next earnings report (days counted from today in New York)">
            <span>Earnings</span><b className={stock.earnings_inside || (earnDays != null && earnDays >= 0 && earnDays <= NORMAL_CC_WINDOW_DAYS) ? 'is-flag' : ''}>{earnDate && earnDays != null && earnDays >= 0 ? `${earnDate} · ${earnDays}d` : '—'}</b>
          </div>
        </section>
      </div>

      {/* Actions */}
      <div className="sc2-ctas">
        {stock.score_breakdown ? <button
          type="button"
          className="sc2-more"
          aria-expanded={showDetails}
          aria-controls={`sc2-details-${stock.symbol}`}
          onClick={(e) => { stop(e); setShowDetails((v) => !v); }}
        >
          More data <ChevronIcon dir={showDetails ? 'up' : 'down'} />
        </button> : <span />}
        <button type="button" className="sc2-cta sc2-cta-secondary" onClick={(e) => { stop(e); setShowAnalysis(true); }}>
          AI read
        </button>
        <button type="button" className="sc2-cta sc2-cta-primary" onClick={(e) => { stop(e); goToContracts(); }}>
          Pick a contract →
        </button>
      </div>

      {showDetails && (
        <div className="sc2-details" id={`sc2-details-${stock.symbol}`} onClick={stop} ref={detailsRef}>
          {/* Custom scoring frameworks keep their own per-indicator breakdown */}
          {stock.score_breakdown && (
            <ScoreBreakdown
              stock={stock}
              activeKPI={null}
              hoveredKPI={null}
              onKPIClick={() => {}}
              onKPIMouseEnter={() => {}}
              onKPIMouseLeave={() => {}}
            />
          )}
          <button type="button" className="sc2-less" onClick={() => setShowDetails(false)}>
            Less data <ChevronIcon dir="up" />
          </button>
        </div>
      )}


      {showMatrix && (
        <SetupMatrixPopover
          symbol={stock.symbol}
          currentCell={stock.setup_cell ?? null}
          anchor={matrixAnchorRef.current}
          onClose={closeMatrix}
          contract={contract}
        />
      )}

      <FullscreenChartModal
        isOpen={showChartModal}
        onClose={() => setShowChartModal(false)}
        symbol={stock.symbol}
        companyName={stock.company_name ?? undefined}
        currentPrice={stock.current_price ?? undefined}
      />

      <StockAnalysisModal
        isOpen={showAnalysis}
        onClose={() => setShowAnalysis(false)}
        symbol={stock.symbol}
        companyName={stock.company_name ?? undefined}
        stockData={stock as any}
      />
    </div>
  );
};

export default StockCard;
